export const headerOptions = [
  {
    value: 'Candidate summary',
    label: 'Candidate summary',
    key: 'candidate_summary',
  },
  {
    value: 'Question and answer',
    label: 'Question and answer',
    key: 'questions',
  },
  {
    value: 'Topic highlights',
    label: 'Topic highlights',
    key: 'topics',
  },
  {
    value: 'Recruiter screen',
    label: 'Candidate fit',
    key: 'recruiter_screen',
  },
  {
    value: 'Highlights & lowlights',
    label: 'Highlights & lowlights',
    key: 'highlights_and_lowlights',
  },
];

export const TIMELINE_COLORS = ['#5653F6', '#2796B8', '#F65397', '#56A073'];

export const INTERVIEW_DETAILS_VIEW_SIZE = [30, 70];

export const SEND_MESSAGE_CONFIG = (
  interviewName: string,
  candidateName: string
) => [
  {
    id: 'need-reschedule',
    text: 'I need this interview rescheduled',
    message: `<p>Unfortunately, I won't be able to make it to the ${interviewName} with ${candidateName}. Can you please reschedule this interview to a later time?</p>`,
    skipCandidate: true,
  },
  {
    id: 'need-more-info',
    text: 'I need more information',
    message: `<p>I am preparing for the ${interviewName} with ${candidateName}. Can you help with more information on the job and ${candidateName}?</p>`,
    skipCandidate: true,
  },
  {
    id: 'late-to-interview',
    text: 'I will be late to the interview',
    message: `<p>Unfortunately, I will be late for ${interviewName} by 5 minutes. Thank you for your patience, and I apologize for the inconvenience.</p>`,
    skipCandidate: false,
  },
  {
    id: 'change-venue',
    text: 'Change in venue or the meeting link',
    message: `<p>Heads-up, there is a slight change in the location of this interview. Please find the updated details below</p>
<p><em>{The updated details go here}</em></p>`,
    skipCandidate: false,
  },
  {
    id: 'custom-message',
    text: 'Custom message',
    message: ``,
    skipCandidate: false,
  },
];

// `className` carries the pastel pair rather than a Tag `variant`: kula's semantic variants
// (success/error/primary) are saturated fills, not pastels — see activityStatusTone.ts.
export const interviewIntelligenceStatusMap = {
  completed: {
    label: 'AI recording ready',
    className: 'bg-pastel-3light text-pastel-3dark',
    value: 'completed',
  },
  failed: {
    label: 'AI recording failed',
    className: 'bg-pastel-1light text-pastel-1dark',
    value: 'failed',
  },
  no_recording: {
    label: 'No AI recording',
    className: 'bg-pastel-1light text-pastel-1dark',
    value: 'no_recording',
  },
  recording_permission_denied: {
    label: 'AI recording denied',
    className: 'bg-pastel-1light text-pastel-1dark',
    value: 'recording_permission_denied',
  },
  scheduled: {
    label: 'AI notetaker enabled',
    className: 'bg-pastel-3light text-pastel-3dark',
    value: 'scheduled',
  },
  pending: {
    label: 'AI pending',
    className: 'bg-pastel-3light text-pastel-3dark',
    value: 'pending',
  },
  recording: {
    label: 'AI recording now',
    className: 'bg-pastel-3light text-pastel-3dark',
    value: 'recording',
  },
  in_waiting_room: {
    label: 'AI waiting',
    className: 'bg-pastel-6light text-pastel-6dark',
    value: 'in_waiting_room',
  },

  processing: {
    label: 'AI processing',
    className: 'bg-pastel-6light text-pastel-6dark',
    value: 'processing',
  },
};

export const interviewStatusMap = {
  not_started: {
    label: 'Interview scheduled',
    bg: 'pastel-2-light',
    color: 'pastel-2-dark',
    value: 'not_started',
  },
  in_progress: {
    label: 'Interview in-progress',
    bg: 'pastel-2-light',
    color: 'pastel-2-dark',
    value: 'in_progress',
  },
  ended: {
    label: 'Interview completed',
    bg: 'pastel-2-light',
    color: 'pastel-2-dark',
    value: 'ended',
  },
  cancelled: {
    label: 'Interview canceled',
    bg: 'pastel-1-light',
    color: 'pastel-1-dark',
    value: 'cancelled',
  },
  candidate_no_show: {
    label: 'Candidate no-show',
    bg: 'pastel-1-light',
    color: 'pastel-1-dark',
    value: 'candidate_no_show',
  },
};

// Merged FE-only intelligence statuses for display/filtering
export const interviewIntelligenceMergedStatusMap = {
  completed: {
    label: 'AI recording ready',
    keys: ['completed'],
  },
  failed: {
    label: 'AI recording failed',
    keys: ['failed', 'no_recording'],
  },
  denied: {
    label: 'AI recording denied',
    keys: ['recording_permission_denied'],
  },
  enabled: {
    label: 'AI notetaker enabled',
    keys: ['scheduled', 'pending'],
  },
  recording: {
    label: 'AI recording now',
    keys: ['recording'],
  },
  waiting: {
    label: 'AI waiting',
    keys: ['in_waiting_room'],
  },
  processing: {
    label: 'AI processing',
    keys: ['processing'],
  },
};

// Tooltip copy for the notetaker status badge, keyed on the Recall event as "<code>:<sub_code>".
// Source: "AI Notetaker recording error tooltips" (Product-owned). A reason with no entry here
// renders no tooltip by design — see ENG-3593.
export const interviewIntelligenceReasonCopyMap: Record<string, string> = {
  // Nobody joined
  'call_ended:meeting_not_started':
    'The meeting had not started when the notetaker joined',
  'call_ended:timeout_exceeded_noone_joined':
    'No participants joined the meeting',
  'call_ended:timeout_exceeded_only_bots_detected_using_participant_events':
    'No participants joined the meeting',
  'call_ended:timeout_exceeded_only_bots_detected_using_participant_names':
    'No participants joined the meeting',
  'call_ended:timeout_exceeded_silence_detected':
    'No audio was detected during the meeting',
  // Never admitted
  'call_ended:bot_kicked_from_call':
    'The notetaker was removed from the meeting',
  'call_ended:bot_kicked_from_waiting_room':
    'The notetaker was removed from the waiting room',
  'call_ended:breakout_room_not_open': 'The breakout room was not open',
  'call_ended:call_ended_by_platform_waiting_room_timeout':
    'The notetaker was not admitted from the waiting room',
  'call_ended:timeout_exceeded_waiting_room':
    'The notetaker was not admitted from the waiting room',
  // Recording never started
  'call_ended:timeout_exceeded_in_call_not_recording':
    'Recording permission was not granted',
  'call_ended:timeout_exceeded_recording_permission_denied':
    'Recording permission was not granted',
  // Link wrong or expired
  'fatal:google_meet_meeting_room_not_ready':
    'The Google Meet room was not ready when the notetaker tried to join',
  'fatal:meeting_ended':
    'The meeting had already ended when the notetaker tried to join',
  'fatal:meeting_link_expired': "This meeting's link expired",
  'fatal:meeting_link_invalid': "This meeting's link was not valid",
  'fatal:meeting_not_accessible': 'The notetaker could not access this meeting',
  'fatal:meeting_not_found': "This meeting's link was not valid",
  'fatal:meeting_not_started':
    'The meeting had not started when the notetaker tried to join',
  'fatal:zoom_meeting_not_accessible':
    'The notetaker could not access this meeting',
  // Needs credentials
  'fatal:meeting_password_incorrect':
    'The notetaker could not join as the meeting password was incorrect',
  'fatal:meeting_requires_registration':
    'The notetaker could not join as the meeting required registration',
  'fatal:meeting_requires_sign_in':
    'The notetaker could not join as the meeting allowed signed-in participants only',
  'fatal:microsoft_teams_2fa_required':
    'The notetaker could not join as the Teams meeting required two-factor authentication',
  'fatal:microsoft_teams_captcha_error':
    'The notetaker could not join as the Teams meeting required a captcha',
  'fatal:microsoft_teams_sign_in_failed':
    'The notetaker could not join as the Teams meeting allowed signed-in participants only',
  'fatal:zoom_captcha_required':
    'The notetaker could not join as the Zoom meeting required a captcha',
  'fatal:zoom_email_required':
    'The notetaker could not join as the Zoom meeting required an email address',
  'fatal:zoom_registration_required':
    'The notetaker could not join as the Zoom meeting required registration',
  // Blocked by policy
  'fatal:google_meet_bot_blocked':
    'The notetaker was blocked from the Google Meet',
  'fatal:google_meet_knocking_disabled':
    'The notetaker could not join as the Google Meet did not allow entry requests',
  'fatal:google_meet_organisation_restricted':
    "The notetaker could not join as the Google Meet was limited to the host's organization",
  'fatal:google_meet_watermark_kicked':
    'The notetaker was removed when watermarking was enabled',
  'fatal:meeting_full':
    'The notetaker could not join as the meeting was at capacity',
  'fatal:meeting_locked':
    'The notetaker could not join as the meeting was locked',
  'fatal:microsoft_teams_bot_not_invited':
    'The notetaker was not invited to the Teams meeting',
  'fatal:zoom_account_blocked':
    'The notetaker was blocked after removal from an earlier Zoom meeting',
  'fatal:zoom_another_meeting_in_progress':
    'The notetaker could not join as the host had another Zoom meeting in progress',
  'fatal:zoom_authorized_participants_only':
    'The notetaker could not join as the Zoom meeting was limited to authorized participants',
  'fatal:zoom_bot_blocked': 'The notetaker was blocked from the Zoom meeting',
  'fatal:zoom_email_blocked_by_admin':
    'The notetaker was blocked by the Zoom workspace admin',
  'fatal:zoom_meeting_host_inactive':
    "The host's Zoom account was inactive or restricted",
  'fatal:zoom_web_disallowed':
    'The notetaker could not join as the Zoom meeting did not allow web access',
  // Meeting type unsupported
  'fatal:google_meet_permission_denied_breakout':
    'The notetaker could not join the Google Meet breakout room',
  'fatal:microsoft_teams_breakout_room_unsupported':
    'The notetaker could not join the Teams breakout room',
  'fatal:microsoft_teams_event_not_started_for_external':
    'The notetaker could not join as the Teams meeting had not started for external guests',
  'fatal:microsoft_teams_townhall_meeting_not_supported':
    'The notetaker could not join as the Teams meeting was a town hall',
  'fatal:zoom_invalid_webinar_invite': 'The Zoom webinar invite was not valid',
  'fatal:zoom_voice_recording_not_supported':
    'The link was a Zoom voice recording, not a live meeting',
  // Internal error
  'fatal:bot_errored':
    'Interview could not be recorded. Contact support for help.',
  'fatal:failed_to_launch_in_time':
    'Interview could not be recorded. Contact support for help.',
  'fatal:google_meet_internal_error':
    'Interview could not be recorded. Contact support for help.',
  'fatal:google_meet_login_not_available':
    'Interview could not be recorded. Contact support for help.',
  'fatal:google_meet_sign_in_captcha_failed':
    'Interview could not be recorded. Contact support for help.',
  'fatal:google_meet_sign_in_failed':
    'Interview could not be recorded. Contact support for help.',
  'fatal:google_meet_sign_in_missing_login_credentials':
    'Interview could not be recorded. Contact support for help.',
  'fatal:google_meet_sign_in_missing_recovery_credentials':
    'Interview could not be recorded. Contact support for help.',
  'fatal:google_meet_sso_sign_in_failed':
    'Interview could not be recorded. Contact support for help.',
  'fatal:google_meet_sso_sign_in_missing_login_credentials':
    'Interview could not be recorded. Contact support for help.',
  'fatal:google_meet_sso_sign_in_missing_totp_secret':
    'Interview could not be recorded. Contact support for help.',
  'fatal:google_meet_video_error':
    'Interview could not be recorded. Contact support for help.',
  'fatal:microsoft_teams_call_dropped':
    'Interview could not be recorded. Contact support for help.',
  'fatal:microsoft_teams_captions_failure':
    'Teams captions were turned off for this meeting',
  'fatal:microsoft_teams_internal_error':
    'Interview could not be recorded. Contact support for help.',
  'fatal:sso_login_account_disabled':
    'Interview could not be recorded. Contact support for help.',
  'fatal:zoom_anonymous_join_not_allowed':
    'Interview could not be recorded. Contact support for help.',
  'fatal:zoom_connection_failed':
    'Interview could not be recorded. Contact support for help.',
  'fatal:zoom_error_multiple_device_join':
    'Interview could not be recorded. Contact support for help.',
  'fatal:zoom_internal_error':
    'Interview could not be recorded. Contact support for help.',
  'fatal:zoom_invalid_join_token':
    'Interview could not be recorded. Contact support for help.',
  'fatal:zoom_invalid_signature':
    'Interview could not be recorded. Contact support for help.',
  'fatal:zoom_join_timeout':
    'Interview could not be recorded. Contact support for help.',
  'fatal:zoom_obf_or_zak_missing_or_invalid':
    'Interview could not be recorded. Contact support for help.',
  'fatal:zoom_obf_user_not_in_meeting':
    'Interview could not be recorded. Contact support for help.',
  'fatal:zoom_sdk_app_not_published':
    'Interview could not be recorded. Contact support for help.',
  'fatal:zoom_sdk_credentials_missing':
    'Interview could not be recorded. Contact support for help.',
  'fatal:zoom_sdk_update_required':
    'Interview could not be recorded. Contact support for help.',
  'fatal:zoom_token_expired':
    'Interview could not be recorded. Contact support for help.',
};
